import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useCreateStripeCheckoutLink } from 'hooks/use-admin-stripe-checkout-links'
import { useProductBundle } from 'hooks/use-product-bundle'
import useValidateHubspotDeal, {
  SandboxInfo,
} from 'hooks/use-validate-hubspot-deal'
import { LineItem } from 'interfaces/line-item'
import { Sandbox } from 'interfaces/sandbox'
import { DiscountCalculator } from 'services/discount-calculator'
import { LineItemManager, LineItemDisplay } from 'services/line-item-manager'
import { HubspotValidator } from 'services/hubspot-validator'
import { ProductHelper } from 'services/product-helper'
import { LineItemStorage } from 'services/line-item-storage'
import { PRODUCT_IDS } from 'constants/product-ids'

export const useSubscriptionPurchaseOrderBuilder = (
  currentUserId?: string | number,
  dealid?: string,
  dealNameFromUrl?: string,
  productIdToPreFetch?: string
) => {
  // Line items
  const [lineItems, setLineItems] = useState<LineItemDisplay[]>([])
  const [savedLineItems, setSavedLineItems] = useState<Partial<LineItem>[]>([])

  // Settings
  const saleRepId = currentUserId
  // HubSpot
  const [hubspotDealLink, setHubspotDealLink] = useState<string>('')
  const [hubspotValidationResult, setHubspotValidationResult] = useState<{
    isValid: boolean
    error?: string
    message?: string
  } | null>(null)

  // Checkout link
  const [checkoutLink, setCheckoutLink] = useState<string | undefined>(
    undefined
  )

  // Sandbox auto-detection
  const [cliInfo, setCliInfo] = useState<{
    partner_integration_id: number
    organization_id: number
  } | null>(null)
  const [sandboxInfo, setSandboxInfo] = useState<SandboxInfo | null>(null)
  const [sandboxSuggestions, setSandboxSuggestions] = useState<SandboxInfo[]>(
    []
  )
  const [sandboxCustomerType, setSandboxCustomerType] = useState<
    'infrastructure' | 'sales_enablement' | null
  >(null)
  const [sandboxPhonePreference, setSandboxPhonePreference] = useState<
    'same' | 'different' | null
  >(null)
  const [salesEnablementAcknowledged, setSalesEnablementAcknowledged] =
    useState(false)
  const [usePhoneFromSandboxId, setUsePhoneFromSandboxId] = useState<
    number | null
  >(null)

  // Manual sandbox search
  const [manualSandboxEnabled, setManualSandboxEnabled] = useState(false)

  // API hooks
  const baseProductInCart = lineItems.find((item) => !item.isAddon)
  // Fetch product bundle for base product in cart OR pre-fetch a specific product ID
  const productIdToFetch =
    productIdToPreFetch || baseProductInCart?.productId || ''

  const { data: productBundle, isLoading: bundleLoading } = useProductBundle(
    productIdToFetch,
    !!productIdToFetch
  )

  const { mutate: stripeCheckoutLink, isLoading: stripeCheckoutLinkLoading } =
    useCreateStripeCheckoutLink()

  const { mutateAsync: validateDeal, isLoading: isValidatingHubspotLink } =
    useValidateHubspotDeal()

  // Load line items from localStorage on mount
  useEffect(() => {
    const { displayItems, savedItems } = LineItemStorage.loadLineItems()
    setLineItems(displayItems)
    setSavedLineItems(savedItems)
  }, [])

  // Save display line items to localStorage whenever they change
  useEffect(() => {
    LineItemStorage.saveDisplayItems(lineItems)
  }, [lineItems])

  // Save saved line items to localStorage whenever they change
  useEffect(() => {
    LineItemStorage.saveSavedItems(savedLineItems)
  }, [savedLineItems])

  const lineItemsKey = useMemo(() => {
    return lineItems
      .map((item) => `${item.productId}:${item.period}:${item.discount ?? 0}`)
      .sort()
      .join('|')
  }, [lineItems])

  // Recalculate automatic discounts whenever line items or product bundle changes
  useEffect(() => {
    if (!productBundle?.prices) return

    let needsUpdate = false
    const updatedLineItems = lineItems.map((item) => {
      const expectedDiscount = DiscountCalculator.getAutomaticDiscount(
        item.productId,
        item.period,
        lineItems.map((i) => ({ productId: i.productId, period: i.period }))
      )

      const isAutomaticDiscount =
        item.productId === PRODUCT_IDS.LINQ_BLUE ||
        item.productId === PRODUCT_IDS.LINQ_ONE

      // Skip if not an automatic discount product or no discount to apply
      if (!isAutomaticDiscount || expectedDiscount === 0) {
        return item
      }

      // Skip if user manually set the discount (respect user's choice)
      if (item.isManualDiscount) {
        return item
      }

      // Only proceed if discount needs to be updated
      if (item.discount === expectedDiscount) {
        return item
      }

      needsUpdate = true

      // Find the original (non-discounted) price for the current period
      let newOriginalPriceId = item.priceId
      const productPrices = productBundle?.prices[item.productId] || []
      const periodInMonths = DiscountCalculator.getPeriodInMonths(item.period)

      const originalPrice = ProductHelper.findOriginalPrice(
        productPrices,
        periodInMonths,
        item.period
      )

      if (originalPrice) {
        newOriginalPriceId = originalPrice.id
      }

      // Find the discounted price ID
      const newDiscountedPriceId = DiscountCalculator.getDiscountPriceId(
        productBundle,
        item.period,
        expectedDiscount,
        item.productId
      )

      return {
        ...item,
        priceId: newOriginalPriceId,
        discount: expectedDiscount,
        discountedPriceId: newDiscountedPriceId,
      }
    })

    if (needsUpdate) {
      setLineItems(updatedLineItems)

      // Sync to savedLineItems to ensure backend receives correct discounted priceId
      const updatedSavedLineItems = savedLineItems.map((savedItem) => {
        // Find matching lineItem by productId to get the updated discountedPriceId
        const matchingLineItem = updatedLineItems.find(
          (lineItem) => lineItem.productId === savedItem.product_id
        )
        const newPrice =
          matchingLineItem?.discountedPriceId ||
          matchingLineItem?.priceId ||
          savedItem.price

        return {
          ...savedItem,
          price: newPrice,
        }
      })
      setSavedLineItems(updatedSavedLineItems)
    }
  }, [lineItemsKey, productBundle?.prices])

  // Populate discountedPriceId when productBundle loads
  useEffect(() => {
    if (!productBundle || !lineItems.length) return

    let needsUpdate = false
    const updatedLineItems = lineItems.map((item) => {
      // Only update if item has a discount but no discountedPriceId yet
      if (item.discount && item.discount > 0 && !item.discountedPriceId) {
        const discountedPriceId = DiscountCalculator.getDiscountPriceId(
          productBundle,
          item.period,
          item.discount,
          item.productId
        )

        if (discountedPriceId) {
          needsUpdate = true
          return {
            ...item,
            discountedPriceId,
          }
        }
      }

      return item
    })

    if (needsUpdate) {
      setLineItems(updatedLineItems)

      // Sync to savedLineItems
      const updatedSavedLineItems = savedLineItems.map((savedItem) => {
        const matchingLineItem = updatedLineItems.find(
          (lineItem) => lineItem.productId === savedItem.product_id
        )
        return {
          ...savedItem,
          price:
            matchingLineItem?.discountedPriceId ||
            matchingLineItem?.priceId ||
            savedItem.price,
        }
      })
      setSavedLineItems(updatedSavedLineItems)
    }
  }, [productBundle?.prices])

  // Pre-fill HubSpot deal link when dealid is in URL
  useEffect(() => {
    if (dealid && !hubspotDealLink) {
      const formattedLink = `https://app.hubspot.com/contacts/7157564/record/0-3/${dealid}`
      setHubspotDealLink(formattedLink)

      setTimeout(async () => {
        try {
          const result = await validateDeal({ dealId: dealid })
          setHubspotValidationResult({ isValid: true })
          setSandboxInfo(result.sandbox || null)
          setSandboxSuggestions(result.sandbox_suggestions || [])
          setCliInfo(result.cli || null)
        } catch (error: any) {
          const errorMessage =
            error.message || 'Please enter a valid Hubspot Deal link.'

          if (errorMessage.toLowerCase().includes('deal id already exist')) {
            setHubspotValidationResult({
              isValid: true,
              message:
                'Subscription already created on this deal. Please check the deal link in Hubspot.',
            })
          } else {
            setHubspotValidationResult({
              isValid: false,
              error: errorMessage,
            })
          }
        }
      }, 100)
    }
  }, [dealid, dealNameFromUrl, validateDeal, hubspotDealLink])

  const handleValidateHubspotDeal = async () => {
    // Validate and extract deal ID using HubspotValidator service
    const validationResult = HubspotValidator.validateLink(hubspotDealLink)

    if (!validationResult.isValid) {
      setHubspotValidationResult({
        isValid: false,
        error: validationResult.error,
      })
      toast.error(validationResult.error || 'Invalid HubSpot deal link.')
      return
    }

    // At this point, validation passed, so extract the deal ID
    const extracted = HubspotValidator.extractId(hubspotDealLink)

    if (!extracted.id) {
      return
    }

    try {
      const result = await validateDeal({ dealId: extracted.id })
      setHubspotValidationResult({ isValid: true })
      setSandboxInfo(result.sandbox || null)
      setSandboxSuggestions(result.sandbox_suggestions || [])
      setCliInfo(result.cli || null)
      // Reset sandbox selections when a new deal is validated
      setSandboxCustomerType(null)
      setSandboxPhonePreference(null)
      setSalesEnablementAcknowledged(false)
      setUsePhoneFromSandboxId(null)
      toast.success('Deal validated successfully!')
    } catch (error: any) {
      const errorMessage =
        error.message || 'Please enter a valid Hubspot Deal link.'

      if (HubspotValidator.isDealAlreadyExistsError(errorMessage)) {
        setHubspotValidationResult({
          isValid: true,
          message:
            'Subscription already created on this deal. Please check the deal link in Hubspot.',
        })
        toast.success('Deal validated successfully!')
      } else {
        setHubspotValidationResult({
          isValid: false,
          error: errorMessage,
        })
        toast.error(errorMessage)
      }
    }
  }

  const handleConfirmSandboxSuggestion = (suggestion: SandboxInfo) => {
    setSandboxInfo(suggestion)
    setSandboxCustomerType(null)
    setSandboxPhonePreference(null)
    setSalesEnablementAcknowledged(false)
    setUsePhoneFromSandboxId(null)
    setCheckoutLink(undefined)
  }

  const handleDismissSandboxSuggestions = () => {
    setSandboxSuggestions([])
  }

  const handleManualSandboxToggle = (enabled: boolean) => {
    setManualSandboxEnabled(enabled)
    if (!enabled) {
      setSandboxInfo(null)
      setSandboxCustomerType(null)
      setSandboxPhonePreference(null)
      setSalesEnablementAcknowledged(false)
      setUsePhoneFromSandboxId(null)
      setCheckoutLink(undefined)
    }
  }

  const handleManualSandboxSelect = (sandbox: Sandbox) => {
    const info: SandboxInfo = {
      id: sandbox.id,
      email: sandbox.email,
      phone_number: sandbox.assigned_phone_number || '',
      status: sandbox.status,
      organization_id: sandbox.organization_id || 0,
      partner_integration_id: sandbox.partner_integration_id || 0,
    }
    setSandboxInfo(info)
    setSandboxCustomerType(null)
    setSandboxPhonePreference(null)
    setSalesEnablementAcknowledged(false)
    setUsePhoneFromSandboxId(null)
    setCheckoutLink(undefined)
  }

  const handleCopyToClipboard = () => {
    if (checkoutLink) {
      navigator.clipboard.writeText(checkoutLink)
      toast.success('Link copied to clipboard!')
    }
  }

  const handleGenerateLink = async (
    settings: {
      hasTrial: boolean
      collectShippingAddress: boolean
      allowCustomerUseReferralCode: boolean
    },
    productName: 'Sales Enablement' | 'One' | 'Infrastructure',
    sandboxOverride?: {
      phonePreference: 'same' | 'different'
      customerType: 'infrastructure' | 'sales_enablement'
    }
  ) => {
    if (checkoutLink) {
      handleCopyToClipboard()
      return
    }

    if (lineItems.length === 0) {
      toast.error('Please add at least one product.')
      return
    }

    if (!saleRepId) {
      toast.error('Please select a Sale Representative.')
      return
    }

    if (!hubspotDealLink) {
      toast.error('Hubspot Deal Link is required.')
      return
    }

    if (!hubspotValidationResult?.isValid) {
      toast.error('Please validate the Hubspot Deal Link first.')
      return
    }

    const finalCustomerType =
      sandboxOverride?.customerType || sandboxCustomerType
    const finalPhonePreference =
      sandboxOverride?.phonePreference || sandboxPhonePreference

    const cleanedLineItems = savedLineItems.map((item) => {
      const cleaned = { ...item }
      delete (cleaned as any).discount
      return cleaned
    })

    stripeCheckoutLink(
      {
        lineItems: cleanedLineItems,
        couponId: undefined,
        saleRepId: saleRepId,
        hasTrial: settings.hasTrial,
        hubspotDealLink: hubspotDealLink,
        mode: 'subscription',
        collectShippingAddress: settings.collectShippingAddress,
        allowCustomerUseReferralCode: settings.allowCustomerUseReferralCode,
        productName: productName,
        ...(sandboxInfo &&
          finalCustomerType &&
          finalPhonePreference && {
            sandboxId: sandboxInfo.id,
            sandboxOrgId: sandboxInfo.organization_id,
            sandboxCustomerType: finalCustomerType,
            sandboxPhonePreference: finalPhonePreference,
            ...(usePhoneFromSandboxId && {
              usePhoneFromSandboxId,
            }),
          }),
        ...(cliInfo && {
          cliConversion: true,
          cliPartnerIntegrationId: cliInfo.partner_integration_id,
        }),
      },
      {
        onSuccess: (data) => {
          setCheckoutLink(data.link)
          navigator.clipboard.writeText(data.link)
          toast.success('Link generated and copied to clipboard!')
        },
        onError: (error: any) => {
          toast.error(error.message || 'Failed to generate link')
        },
      }
    )
  }

  const totalAmount = LineItemManager.calculateTotalAmount(lineItems)

  const hasLinqOneInCart = lineItems.some(
    (item) => item.productId === PRODUCT_IDS.LINQ_ONE
  )

  const hasOnlyLinqOne =
    hasLinqOneInCart &&
    lineItems.every(
      (item) =>
        item.productId === PRODUCT_IDS.LINQ_ONE ||
        ![
          PRODUCT_IDS.LINQ_BLUE,
          PRODUCT_IDS.LINQ_INFRA,
          PRODUCT_IDS.LINQ_INFRA_200_CONTACT,
          PRODUCT_IDS.LINQ_SAPPHIRE,
        ].includes(item.productId)
    )

  return {
    // Line items (core data)
    lineItems,
    setLineItems,
    savedLineItems,
    setSavedLineItems,
    saleRepId,
    // HubSpot
    hubspotDealLink,
    setHubspotDealLink,
    hubspotValidationResult,
    setHubspotValidationResult,
    // Checkout
    checkoutLink,
    setCheckoutLink,
    // Sandbox
    sandboxInfo,
    sandboxSuggestions,
    sandboxCustomerType,
    setSandboxCustomerType,
    sandboxPhonePreference,
    setSandboxPhonePreference,
    salesEnablementAcknowledged,
    setSalesEnablementAcknowledged,
    usePhoneFromSandboxId,
    setUsePhoneFromSandboxId,
    handleConfirmSandboxSuggestion,
    handleDismissSandboxSuggestions,
    manualSandboxEnabled,
    handleManualSandboxToggle,
    handleManualSandboxSelect,
    // CLI
    cliInfo,
    // Product bundle
    productBundle,
    bundleLoading,
    // Loading states
    stripeCheckoutLinkLoading,
    isValidatingHubspotLink,
    // Computed values
    totalAmount,
    hasOnlyLinqOne,
    // Methods (core business logic)
    handleGenerateLink,
    handleValidateHubspotDeal,
  }
}
